import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Briefcase, GraduationCap, Sparkle, MapPin, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Profile() {
  const { profile, setProfile, refreshProfile } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!profile) refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profile === null) {
      // after refresh, if still null redirect
      (async () => {
        setLoading(true);
        const data = await refreshProfile();
        setLoading(false);
        if (!data?.profile) nav("/onboarding");
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
          <div className="h-48 border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm animate-pulse bg-slate-50/50" />
        </div>
      </div>
    );
  }

  const skills = profile.skills || [];
  const exp = profile.experience || [];
  const projects = profile.projects || [];
  const edu = profile.education || [];
  const roles = profile.preferred_roles || [];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="tick-label">Profile · Scope</span>
            <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl tracking-tighter">
              {profile.full_name || "Your profile"}
            </h1>
            {profile.headline && <p className="mt-2 text-ink-soft">{profile.headline}</p>}
          </div>
          <Button onClick={() => nav("/onboarding")} variant="outline" className="rounded-xl border-slate-200 text-ink hover:bg-ink hover:text-white uppercase tracking-wider text-xs h-11 px-5" data-testid="edit-profile-btn">
            <PencilSimple size={14} weight="bold" className="mr-2" />Edit profile
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm mb-10">
          <Stat label="Years of exp" value={profile.experience_years ?? 0} />
          <Stat label="Seniority" value={(profile.seniority || "—").toUpperCase()} />
          <Stat label="Skills" value={skills.length} />
          <Stat label="Source" value={(profile.source || "manual").toUpperCase()} />
        </div>

        {profile.summary && (
          <Section title="Summary">
            <p className="text-sm leading-relaxed text-ink-soft border-l-2 border-brand pl-4">{profile.summary}</p>
          </Section>
        )}

        <Section title="Skills">
          <div className="flex flex-wrap gap-2" data-testid="profile-skills">
            {skills.length === 0 && <span className="text-sm text-ink-mute">No skills captured.</span>}
            {skills.map((s) => (
              <Badge key={s} className="rounded-xl bg-brand-muted text-brand border-0 uppercase tracking-wider text-[10px]">{s}</Badge>
            ))}
          </div>
        </Section>

        {roles.length > 0 && (
          <Section title="Target roles">
            <div className="flex flex-wrap gap-2">
              {roles.map((r) => (
                <Badge key={r} className="rounded-xl bg-white text-ink border border-slate-200 uppercase tracking-wider text-[10px]">{r}</Badge>
              ))}
            </div>
          </Section>
        )}

        {exp.length > 0 && (
          <Section title="Experience">
            <div className="divide-y divide-border border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm">
              {exp.map((e, i) => (
                <div key={i} className="p-5 flex items-start gap-4">
                  <Briefcase size={20} weight="duotone" className="text-brand mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-display font-bold text-lg">{e.role}</h4>
                    <p className="text-sm text-ink-soft">{e.company} · {e.duration}</p>
                    {e.highlights?.length > 0 && (
                      <ul className="mt-3 space-y-1.5 text-sm text-ink-soft list-disc pl-5">
                        {e.highlights.slice(0, 4).map((h, j) => <li key={j}>{h}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {projects.length > 0 && (
          <Section title="Projects">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-border">
              {projects.map((p, i) => (
                <div key={i} className="border-r border-b border-border p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkle size={14} weight="fill" className="text-brand" />
                    <h4 className="font-display font-bold">{p.name}</h4>
                  </div>
                  <p className="text-sm text-ink-soft">{p.description}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {edu.length > 0 && (
          <Section title="Education">
            <div className="divide-y divide-border border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm">
              {edu.map((e, i) => (
                <div key={i} className="p-5 flex items-start gap-4">
                  <GraduationCap size={20} weight="duotone" className="text-brand mt-0.5" />
                  <div>
                    <h4 className="font-display font-bold">{e.school}</h4>
                    <p className="text-sm text-ink-soft">{e.degree} · {e.year}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-5 border-r border-b md:border-b-0 border-border last:border-r-0">
      <span className="tick-label">{label}</span>
      <div className="mt-2 font-mono font-black text-2xl">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="tick-label">{title}</span>
        <span className="h-px flex-1 bg-border" />
      </div>
      {children}
    </section>
  );
}
